import { AnimatePresence, m, PanInfo, useDragControls } from "framer-motion";
import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

/**
 * A bottom sheet, the way iOS does one: it rises on a spring, dims what's
 * behind it, follows a downward drag one-for-one, and drops away when you
 * let go past halfway.
 *
 * It renders through a portal into `document.body` rather than where it's
 * written. That isn't tidiness — it's the only way it works. A sheet is
 * `position: fixed`, and a fixed element is positioned against the viewport
 * *only* while no ancestor carries a transform; the moment one does, the
 * nearest transformed ancestor becomes its containing block and that
 * ancestor's `overflow` clips it. Both are true of where these get used: the
 * roster is an `overflow-hidden` card inside a `<FadeIn>`, which animates `y`.
 * Written inline, the sheet was being cropped to the card — losing its
 * corners and leaving a slab of panel behind. Portalled, it has no ancestor
 * but `<body>` and nothing can crop it.
 */

/** How far down, or how briskly, a drag has to go before it counts as a dismiss. */
const DISMISS_OFFSET = 96;
const DISMISS_VELOCITY = 520;

// Rising: a spring, because the sheet arriving should have some weight behind
// it. Leaving: a tween that accelerates away (ease-in), because a spring's
// long settle reads as hesitation on the way out. iOS does exactly this pair.
const RISE = { type: "spring" as const, damping: 36, stiffness: 400, mass: 0.85 };
const FALL = { duration: 0.22, ease: [0.32, 0, 0.67, 0] as const };

export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /** Shown as the sheet's heading, and used as its accessible name. */
  title?: string;
  children: ReactNode;
}) {
  // Escape closes it, and the page behind stops scrolling while it's up —
  // both things a native sheet does and a bare div doesn't.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  const onDragEnd = (_: unknown, info: PanInfo) => {
    // Either a long enough pull or a quick enough flick — a fast short flick
    // is a dismiss too, which is the part that makes it feel like iOS rather
    // than like a threshold being measured.
    if (info.offset.y > DISMISS_OFFSET || info.velocity.y > DISMISS_VELOCITY) onClose();
  };

  // `drag` on the sheet used to listen everywhere on it, which meant a swipe
  // over the body was captured as a dismiss before it ever became a scroll —
  // there was nowhere to scroll a long list at all. `dragListener={false}`
  // turns that global listener off; `dragControls.start` on the grabber's own
  // pointerdown is the only thing that can still begin a drag, so the body
  // is free to scroll and the grabber is still what you'd reach for to pull
  // the sheet away.
  const dragControls = useDragControls();

  return createPortal(
    <AnimatePresence>
      {open && (
        <m.div
          key="sheet"
          className="fixed inset-0 z-50 flex flex-col justify-end bg-ink/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={onClose}
        >
          <m.div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e: MouseEvent) => e.stopPropagation()}
            className="w-full max-w-md mx-auto bg-paper text-ink rounded-t-[20px] flex flex-col"
            initial={{ y: "100%" }}
            animate={{ y: 0, transition: RISE }}
            exit={{ y: "100%", transition: FALL }}
            drag="y"
            dragListener={false}
            dragControls={dragControls}
            // Constrained at both edges so a release that *doesn't* dismiss
            // springs back on its own. Upward is a hard stop (elastic 0) —
            // the sheet has nowhere higher to go. Downward is all but
            // frictionless, so it tracks the finger instead of lagging it.
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.9 }}
            dragMomentum={false}
            dragTransition={{ bounceStiffness: 400, bounceDamping: 40 }}
            onDragEnd={onDragEnd}
          >
            {/* The grabber. Says "you can pull this" before anyone tries, and
                is now the only place a pull can start from — everything below
                it is free to scroll instead. */}
            <div
              onPointerDown={(e) => dragControls.start(e)}
              className="flex justify-center pt-3 pb-3.5 px-7 cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
            >
              <div className="w-9 h-[3px] bg-line rounded-full" />
            </div>
            {title && (
              <div className="font-display text-[15px] font-bold uppercase tracking-tight px-7 mb-4 flex-shrink-0">
                {title}
              </div>
            )}
            {/* Capped and scrollable in its own right, rather than growing
                the sheet past the top of the screen: a trail with enough
                entries needs somewhere to go that isn't off-screen. */}
            <div
              className="px-7 pt-2 overflow-y-auto overscroll-contain"
              style={{
                maxHeight: "calc(var(--app-height, 100svh) - 30vh)",
                // max, not +: the home indicator's own clearance is already
                // the gap this wanted. See MarkPaidSheet, which learned it
                // first.
                paddingBottom: "max(1.5rem, var(--safe-bottom))",
              }}
            >
              {children}
            </div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
